import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, updateStaff } from '../../lib/supabase';
import { ensureEntityFolder } from '../../lib/driveRoots';
import DataTable from '../../components/DataTable';
import { useRole } from '../../components/RoleGuard';
import { getCaregivers, updateCaregiver } from '../../lib/axiscare';
import { pushCaregiverToAxisCare } from '../../lib/axiscarePush';
import { bgCheckTypeFor } from '../../lib/staffCompliance';
import { StatTile } from '../../components/StatTile';
import { GA_COUNTIES } from '../../lib/gaCounties';
import { RESTRICTION_OPTIONS } from '../../lib/pets';

// Urgent-hire fast track (2026-08-08) — sometimes a contractor needs to be
// scheduling shifts today, with no time to run the full Onboarding Pipeline
// (offer letter → background check consent → GCHEXS → Systems & Access
// Setup) first. Quick Add creates the staff record as active immediately and
// pushes straight to AxisCare, skipping all of that — deliberately, per an
// explicit product decision to prioritize speed over gating here. The
// safety net isn't a gate, it's visibility: every fast-tracked record is
// permanently flagged (fast_tracked_at) and shows up in the Personnel File
// checklist on their profile exactly like any other incomplete record,
// with "Send request" actions there to catch up the paperwork after the
// fact (see PersonnelProfile.js).
const QUICK_EMPTY_FORM = {
  first_name: '', last_name: '', phone: '', email: '',
  date_of_birth: '', ssn_last4: '', address: '', city: '', state: 'GA', zip: '', county: '',
  employment_type: 'agency', position_code: '', caregiver_relationship: '',
  pay_rate: '', pay_type: 'hourly', start_date: new Date().toISOString().slice(0, 10),
  contracting_agency_id: '',
};

const STATUS_OPTS = ['active', 'inactive', 'on_leave', 'terminated'];
const PAY_TYPES   = ['hourly', 'weekly', 'bi_weekly', 'monthly', 'annually', 'per_visit'];
const EMP_TYPES   = ['employee', 'contractor', 'agency', 'per_diem'];
const GENDER_OPTS  = ['Male', 'Female', 'Other'];
const LANG_OPTS    = [{ value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }];

const EMPTY_FORM = {
  first_name: '', last_name: '', suffix: '', goes_by: '', email: '', phone: '',
  date_of_birth: '', gender: '', pet_restrictions: [], pet_restrictions_notes: '',
  address: '', city: '', state: 'GA', zip: '', county: '',
  position_code: '', is_lead: false, is_administrator_of_record: false, caregiver_relationship: '',
  employment_type: 'employee',
  pay_rate: '', pay_type: 'hourly',
  start_date: '', status: 'active', notes: '',
  preferred_language: 'en', supervisor_id: '',
  termination_date: '', termination_reason: '', rehire_eligible: '',
  contracting_agency_id: '',
};

// Occupation is a real DB table now (roles) — this is just a display-color
// lookup, not the source of truth for what roles exist.
const roleColor = code => ({
  RN:      { bg: 'var(--accent-bg)',  color: 'var(--accent2)' },
  LPN:     { bg: 'var(--accent-bg)',  color: 'var(--accent2)' },
  PCA:     { bg: 'var(--success-bg)', color: 'var(--success)' },
  PCC:     { bg: 'var(--info-bg)',    color: 'var(--info)' },
  DIR_OPS: { bg: 'var(--copper-bg)',  color: 'var(--copper)' },
  BOC:     { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  MGR:     { bg: 'var(--copper-bg)',  color: 'var(--copper)' },
}[code] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const statusColor = s => ({
  active:     { bg: 'var(--success-bg)', color: 'var(--success)' },
  inactive:   { bg: 'var(--bg2)',        color: 'var(--text3)' },
  on_leave:   { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  terminated: { bg: 'var(--danger-bg)',  color: 'var(--danger)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

// Defined at module scope (not inside Personnel) so its identity is
// stable across renders — otherwise every keystroke's re-render creates a
// new F function, React treats it as a different component type, and the
// wrapped <input> gets unmounted/remounted, dropping focus every letter.
const F = ({ label, children, hint }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    {children}
    {hint && <div className="form-hint">{hint}</div>}
  </div>
);

export default function Personnel() {
  const navigate = useNavigate();
  const location = useLocation();
  // ?missing=dob — active staff with no date of birth, from the dashboard's
  // birthday banner, so the gap can be filled in one list.
  const missingDob = new URLSearchParams(location.search).get('missing') === 'dob';
  const [staff,      setStaff]      = useState([]);
  const [roles,      setRoles]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [syncing,    setSyncing]    = useState(false);
  const [filter,     setFilter]     = useState('All');
  const [agencyFilter, setAgencyFilter] = useState('All');
  const [search,     setSearch]     = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [axisWarning, setAxisWarning] = useState('');
  const [axisError,  setAxisError]  = useState('');
  const [lastSync,   setLastSync]   = useState(null);
  const { isAdmin } = useRole();
  const [showHidden, setShowHidden] = useState(false);
  const [contractingAgencies, setContractingAgencies] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickForm, setQuickForm] = useState(QUICK_EMPTY_FORM);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickError, setQuickError] = useState('');
  const [axisPushBusyId, setAxisPushBusyId] = useState(null);

  useEffect(() => { load(); loadRoles(); loadContractingAgencies(); }, []);

  const loadContractingAgencies = async () => {
    const { data } = await supabase.from('contracting_agencies').select('*').order('name');
    setContractingAgencies(data || []);
  };

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('staff')
      .select('*, profiles(full_name, role, email, modules), contracting_agency:contracting_agencies(id, name)')
      .order('last_name');
    setStaff(data || []);
    setLoading(false);
  };

  const loadRoles = async () => {
    const { data } = await supabase.from('roles').select('*').eq('active', true).order('label');
    setRoles(data || []);
  };
  const roleLabel = code => roles.find(r => r.code === code)?.label || code;

  // Auto-open the edit modal for a specific person when arriving back from
  // the Personnel Profile page's Edit button (state.editId).
  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId || staff.length === 0) return;
    const match = staff.find(s => s.id === editId);
    if (match) openEdit(match);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, location.state?.editId]);

  // ── Sync AxisCare caregivers into staff table ─────────────────────────────
  // Legacy/secondary intake path — new hires now come in through the
  // Apply Form → Onboarding Pipeline, which populates `staff` directly.
  const syncAxisCare = async () => {
    setSyncing(true);
    setAxisError('');
    const { data, error } = await getCaregivers();
    if (error || !data) {
      setAxisError('Could not reach AxisCare — check API connection');
      setSyncing(false);
      return;
    }

    // AxisCare returns caregivers as an object keyed by ID, not an array
    // e.g. { results: { caregivers: { "1": {...}, "2": {...} } } }
    const raw = data?.results?.caregivers || data?.caregivers || data;
    const caregivers = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object'
        ? Object.values(raw)
        : [];

    if (caregivers.length === 0) {
      setAxisError('No caregivers returned from AxisCare — check API connection');
      setSyncing(false);
      return;
    }
    let added = 0, skipped = 0;

    // Reverse-lookup AxisCare's own "class" code -> our position_code, so a
    // newly-discovered caregiver lands with a real role instead of none.
    const { data: mappings } = await supabase.from('role_external_mappings').select('*').eq('system', 'axiscare');
    const byExternalCode = {};
    (mappings || []).forEach(m => { byExternalCode[m.external_code] = m; });

    // CAERA is the source of truth for onboarding now — basic data
    // (name/contact/pay/type/status) is only pulled from AxisCare the very
    // first time a caregiver is discovered here. Once a staff record
    // exists, this sync never touches it again; it only looks for
    // brand-new AxisCare caregivers we don't have yet. Status changes flow
    // the other direction now — see the push in handleSave below.
    for (const c of caregivers) {
      const axId = String(c.id);

      const { data: existing } = await supabase
        .from('staff').select('id, hidden').eq('axiscare_id', axId).single();

      if (existing) {
        skipped++;
        continue;
      }

      const axisClass = c.classes?.[0]?.code;
      const mapped = axisClass ? byExternalCode[axisClass] : null;

      const { data: newStaffRow } = await supabase.from('staff').insert([{
        full_name:       `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        first_name:      (c.firstName || '').trim() || null,
        last_name:       (c.lastName || '').trim() || null,
        goes_by:         c.goesBy || null,
        email:           c.personalEmail || c.workEmail || null,
        phone:           c.mobilePhone   || c.homePhone  || null,
        position_code:   mapped?.role_code || null,
        is_lead:         mapped?.is_lead || false,
        caregiver_relationship: mapped && mapped.caregiver_relationship !== 'any' ? mapped.caregiver_relationship : null,
        source:          'axiscare',
        axiscare_id:     axId,
        status:          c.status?.active ? 'active' : 'inactive',
        pay_rate:        c.payRate || null,
        pay_type:        'hourly',
        employment_type: 'employee',
        hidden:          false,
      }]).select('id, employee_id').single();
      if (newStaffRow?.employee_id) {
        ensureEntityFolder('staff', newStaffRow.employee_id).then(folderId => folderId && updateStaff(newStaffRow.id, { drive_folder_id: folderId }));
      }
      added++;
    }

    setLastSync(new Date());
    setSuccess(`AxisCare sync complete — ${added} added, ${skipped} already on file (unchanged)`);
    setTimeout(() => setSuccess(''), 4000);
    load();
    setSyncing(false);
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      first_name:      s.first_name || '',
      last_name:       s.last_name || '',
      suffix:          s.suffix || '',
      goes_by:         s.goes_by || '',
      email:           s.email || '',
      phone:           s.phone || '',
      date_of_birth:   s.date_of_birth || '',
      pet_restrictions: s.pet_restrictions || [],
      pet_restrictions_notes: s.pet_restrictions_notes || '',
      ssn_last4:       s.ssn_last4 || '',
      gender:          s.gender || '',
      address:         s.address || '',
      city:            s.city || '',
      state:           s.state || 'GA',
      zip:             s.zip || '',
      county:          s.county || '',
      position_code:   s.position_code || '',
      is_lead:         s.is_lead || false,
      is_administrator_of_record: s.is_administrator_of_record || false,
      caregiver_relationship:     s.caregiver_relationship || '',
      employment_type: s.employment_type || 'employee',
      pay_rate:        s.pay_rate || '',
      pay_type:        s.pay_type || 'hourly',
      start_date:      s.start_date || '',
      status:          s.status || 'active',
      notes:           s.notes || '',
      preferred_language: s.preferred_language || 'en',
      supervisor_id:      s.supervisor_id || '',
      termination_date:   s.termination_date || '',
      termination_reason: s.termination_reason || '',
      rehire_eligible:    s.rehire_eligible === true ? 'true' : s.rehire_eligible === false ? 'false' : '',
      contracting_agency_id: s.contracting_agency_id || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { setError('First and last name are required'); return; }

    // Soft duplicate check — warn, don't block. Common names are legitimate.
    if (!editing) {
      const { data: possibleDupes } = await supabase
        .from('staff')
        .select('full_name, position_code, status')
        .ilike('first_name', form.first_name)
        .ilike('last_name', form.last_name)
        .eq('hidden', false);
      if (possibleDupes?.length) {
        const list = possibleDupes.map(s => `${s.full_name} — ${s.position_code || 'no role set'}, ${s.status}`).join('\n');
        const proceed = window.confirm(
          `A staff member named "${form.first_name} ${form.last_name}" already exists:\n\n${list}\n\nAdd a new, separate record anyway?`
        );
        if (!proceed) return;
      }
    }

    setSaving(true);
    setError('');

    const fullName = `${form.first_name} ${form.last_name}`.trim();
    const payload = {
      ...form,
      full_name: fullName,
      position_code: form.position_code || null,
      caregiver_relationship: form.position_code === 'PCA' ? (form.caregiver_relationship || null) : null,
      is_lead: !!form.is_lead,
      is_administrator_of_record: !!form.is_administrator_of_record,
      pay_rate: form.pay_rate ? parseFloat(form.pay_rate) : null,
      start_date: form.start_date || null,
      date_of_birth: form.date_of_birth || null,
      pet_restrictions: form.pet_restrictions?.length ? form.pet_restrictions : null,
      pet_restrictions_notes: (form.pet_restrictions_notes || '').trim() || null,
      ssn_last4: /^[0-9]{4}$/.test(form.ssn_last4 || '') ? form.ssn_last4 : null,
      supervisor_id: form.supervisor_id || null,
      contracting_agency_id: form.employment_type === 'agency' ? (form.contracting_agency_id || null) : null,
      termination_date: form.termination_date || null,
      rehire_eligible: form.rehire_eligible === 'true' ? true : form.rehire_eligible === 'false' ? false : null,
      updated_at: new Date().toISOString(),
    };

    const { data: savedStaff, error: err } = editing
      ? await supabase.from('staff').update(payload).eq('id', editing.id).select('id, employee_id').single()
      : await supabase.from('staff').insert([{ ...payload, source: 'manual' }]).select('id, employee_id').single();

    if (err) { setError(err.message); setSaving(false); return; }

    if (!editing && savedStaff?.employee_id) {
      ensureEntityFolder('staff', savedStaff.employee_id).then(folderId => folderId && updateStaff(savedStaff.id, { drive_folder_id: folderId }));
    }

    // CAERA is now authoritative for status — push the change to AxisCare so
    // a deactivated caregiver can't still be scheduled/clocked in over there.
    // Best-effort: the CAERA-side save already succeeded, so a push failure
    // is surfaced but doesn't roll anything back. Shown as its own amber
    // warning, not folded into the green success banner — a push failure
    // means the caregiver may still be schedulable/clockable in AxisCare,
    // which reads very differently from "✓ saved successfully".
    let pushWarning = '';
    if (editing?.axiscare_id && editing.status !== form.status) {
      const axisStatus = form.status === 'active' ? 'Active' : 'Inactive';
      const { data: pushData, error: pushErr } = await updateCaregiver(editing.axiscare_id, { status: axisStatus });
      if (pushErr || pushData?.errors?.length) {
        pushWarning = `Could not update status in AxisCare: ${pushErr?.message || pushData.errors[0]} — this caregiver may still show as ${editing.status === 'active' ? 'Active' : 'Inactive'} there.`;
      }
    }

    await load();
    setShowModal(false);
    setSuccess(`${fullName} ${editing ? 'updated' : 'added'} successfully`);
    setTimeout(() => setSuccess(''), 3000);
    if (pushWarning) {
      setAxisWarning(pushWarning);
      setTimeout(() => setAxisWarning(''), 8000);
    }
    setSaving(false);
  };

  // ── Quick Add (urgent contractor fast-track) ──────────────────────────────
  const openQuickAdd = () => {
    setQuickForm(QUICK_EMPTY_FORM);
    setQuickError('');
    setShowQuickAdd(true);
  };

  const handleQuickSave = async () => {
    if (!quickForm.first_name.trim() || !quickForm.last_name.trim()) { setQuickError('First and last name are required'); return; }
    setQuickSaving(true);
    setQuickError('');

    const fullName = `${quickForm.first_name} ${quickForm.last_name}`.trim();
    const now = new Date().toISOString();
    const payload = {
      first_name: quickForm.first_name.trim(), last_name: quickForm.last_name.trim(), full_name: fullName,
      phone: quickForm.phone || null, email: quickForm.email || null,
      date_of_birth: quickForm.date_of_birth || null,
      address: quickForm.address || null, city: quickForm.city || null,
      state: quickForm.state || null, zip: quickForm.zip || null, county: quickForm.county || null,
      employment_type: quickForm.employment_type,
      position_code: quickForm.position_code || null,
      caregiver_relationship: quickForm.position_code === 'PCA' ? (quickForm.caregiver_relationship || null) : null,
      contracting_agency_id: quickForm.employment_type === 'agency' ? (quickForm.contracting_agency_id || null) : null,
      pay_rate: quickForm.pay_rate ? parseFloat(quickForm.pay_rate) : null,
      pay_type: quickForm.pay_type,
      start_date: quickForm.start_date || null,
      // 2026-09-07: Quick Add no longer bypasses the paperwork. The person is
      // active immediately (scheduling + payroll) but lands in the Onboarding
      // Pipeline's Candidate lane with "Already working" pre-set, so the
      // bundle still goes out for one signature. This is what produced the
      // duplicate records before — nothing on file, so the application got
      // converted into a second row.
      status: 'active', onboarding_status: 'candidate', fast_tracked_at: now,
      hire_track: quickForm.employment_type,
      bg_check_type: bgCheckTypeFor({ position_code: quickForm.position_code }),
      source: 'manual',
    };

    const { data: quickStaff, error: err } = await supabase.from('staff').insert([payload]).select().single();
    if (err) { setQuickError(err.message); setQuickSaving(false); return; }

    if (quickStaff?.employee_id) {
      ensureEntityFolder('staff', quickStaff.employee_id).then(folderId => folderId && updateStaff(quickStaff.id, { drive_folder_id: folderId }));
    }

    // AxisCare is created when the bundle is signed (submit-staff-signatures,
    // 2026-09-07). The record shows up active with no axiscare_id, so the
    // Actions column offers "Push to AxisCare early" for the rare case where
    // someone must clock in before they have signed.
    await load();
    setShowQuickAdd(false);
    setQuickSaving(false);
    setSuccess(`${fullName} is active and can be scheduled now. Send their bundle from the Onboarding Pipeline (Candidate lane, "Already working" is pre-set) — signing it creates their AxisCare account and sends the welcome email.`);
    setTimeout(() => setSuccess(''), 8000);
  };

  // Manual fallback. Since 2026-09-07 the signing function creates the
  // AxisCare caregiver automatically; this remains for (a) an auto-create
  // that failed — the reason is on the person's timeline and on Training &
  // Compliance › Onboarding follow-ups — and (b) someone who must clock in
  // before they have signed.
  const pushToAxisCareRetry = async (s) => {
    setAxisPushBusyId(s.id);
    setAxisError('');
    try {
      const axisId = await pushCaregiverToAxisCare(s);
      await supabase.from('staff').update({ axiscare_id: axisId, axiscare_created_at: new Date().toISOString() }).eq('id', s.id);
      setSuccess(`${s.full_name} created in AxisCare.`);
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (err) {
      setAxisError(`AxisCare push failed for ${s.full_name}: ${err.message}`);
    } finally {
      setAxisPushBusyId(null);
    }
  };

  const handleDelete = async (s) => {
    if (s.source === 'axiscare') {
      alert('AxisCare caregivers are managed in AxisCare. They will reappear on the next sync if deleted here.');
      return;
    }
    if (s.profile_id) {
      alert(`${s.full_name} has a CAERA login. Remove their user account first in Settings → User Management before removing their personnel record.`);
      return;
    }
    if (!window.confirm(`Remove ${s.full_name} from Personnel? This cannot be undone.`)) return;
    const { error: err } = await supabase.from('staff').delete().eq('id', s.id);
    if (err) { setError(`Delete failed: ${err.message}`); return; }
    setSuccess(`${s.full_name} removed from Personnel`);
    setTimeout(() => setSuccess(''), 3000);
    load();
  };

  const handleHide = async (s) => {
    if (!window.confirm(`Permanently hide ${s.full_name}? They won't appear in Personnel or sync updates. You can reveal them later via "Show hidden".`)) return;
    const { error: err } = await supabase.from('staff').update({ hidden: true }).eq('id', s.id);
    if (err) { setError(`Hide failed: ${err.message}`); return; }
    setSuccess(`${s.full_name} hidden from Personnel`);
    setTimeout(() => setSuccess(''), 3000);
    load();
  };

  // ── Filter & search ───────────────────────────────────────────────────────
  const filtered = staff.filter(s => {
    // Hidden filter — show hidden records only if toggle is on
    const hiddenMatch = showHidden ? true : !s.hidden;
    const typeMatch = filter === 'All' || s.position_code === filter;
    // 'Direct' = anyone we engage ourselves; 'Any agency' = sourced through a
    // contracting agency, whichever one. Agency attribution drives both the
    // payroll run's pay-vs-agency-payable split and the staffing board, so it
    // needs to be filterable, not just visible one record at a time.
    const agencyMatch =
      agencyFilter === 'All'    ? true
      : agencyFilter === 'none' ? s.employment_type !== 'agency' && !s.contracting_agency_id
      : agencyFilter === 'any'  ? !!s.contracting_agency_id
      : s.contracting_agency_id === agencyFilter;
    const searchMatch = !search || s.full_name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase());
    const dobMatch = !missingDob || (s.status === 'active' && !s.date_of_birth);
    return hiddenMatch && typeMatch && agencyMatch && searchMatch && dobMatch;
  });

  const visible = staff.filter(s => !s.hidden);
  const stats = {
    total:     visible.length,
    active:    visible.filter(s => s.status === 'active').length,
    axiscare:  visible.filter(s => s.source === 'axiscare').length,
    hasLogin:  visible.filter(s => s.profile_id).length,
    noRate:    visible.filter(s => !s.pay_rate && s.status === 'active').length,
  };

  const goToProfile = (s) => navigate(`/hr/personnel/${s.id}`, { state: { staffIds: filtered.map(f => f.id) } });

  const pill = (label, style) => (
    <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, ...style }}>{label}</span>
  );

  const columns = [
    {
      accessorKey: 'employee_id', header: 'Employee ID', size: 100,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue() || <span style={{ color: 'var(--text3)', fontStyle: 'italic', fontFamily: 'inherit' }} title="Assigned once the offer is signed">pending</span>,
    },
    {
      id: 'name', accessorKey: 'last_name', header: 'Name', size: 190,
      meta: { mobile: 'primary' },
      cell: info => {
        const s = info.row.original;
        return (
          <div>
            <div
              style={{ fontWeight: 500, color: 'var(--accent)', cursor: 'pointer' }}
              onClick={() => goToProfile(s)}
              title="View Personnel Profile"
            >
              {s.last_name}, {s.first_name}
            </div>
            {s.position_code && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{roleLabel(s.position_code)}</div>}
            {s.hidden && <span style={{ fontSize: 9, background: 'var(--warn-bg)', color: 'var(--warn)', padding: '1px 5px', borderRadius: 3 }}>HIDDEN</span>}
            {s.fast_tracked_at && <span title={`Fast-tracked ${new Date(s.fast_tracked_at).toLocaleDateString()} — skipped the normal Onboarding Pipeline`} style={{ fontSize: 9, background: 'var(--warn-bg)', color: 'var(--warn)', padding: '1px 5px', borderRadius: 3, marginLeft: 4 }}>⚡ FAST-TRACKED</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'position_code', header: 'Position', size: 170,
      meta: { mobile: 'secondary' },
      cell: info => {
        const code = info.getValue();
        const s = info.row.original;
        if (!code) return <span style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }} title="Assigned once the offer is signed">pending</span>;
        const rc = roleColor(code);
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {pill(roleLabel(code), { background: rc.bg, color: rc.color })}
            {s.is_lead && pill('Lead', { background: 'var(--copper-bg)', color: 'var(--copper)' })}
          </div>
        );
      },
    },
    {
      accessorKey: 'employment_type', header: 'Employment', size: 120,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ fontSize: 12 }}>{info.getValue()?.replace('_', ' ')}</span>,
    },
    {
      // Agency attribution is the join the staffing board and the payroll
      // run's agency-payable split both depend on, so a mismatch between
      // employment_type and contracting_agency_id is called out here rather
      // than left to be discovered one record at a time.
      id: 'agency', accessorFn: s => s.contracting_agency?.name || '', header: 'Agency', size: 170,
      meta: { mobile: 'hidden' },
      cell: info => {
        const s = info.row.original;
        if (s.contracting_agency?.name) {
          return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              {pill(s.contracting_agency.name, { background: 'var(--info-bg)', color: 'var(--info)' })}
              {s.employment_type !== 'agency' && (
                <span title="Linked to an agency but not marked as an agency worker — the payroll split and staffing board will treat this person as direct." style={{ fontSize: 11, color: 'var(--warn)' }}>⚠</span>
              )}
            </div>
          );
        }
        if (s.employment_type === 'agency') {
          return <span title="Marked as an agency worker but no contracting agency is set." style={{ fontSize: 11, background: 'var(--warn-bg)', color: 'var(--warn)', padding: '2px 7px', borderRadius: 4 }}>agency not set</span>;
        }
        return <span style={{ fontSize: 12, color: 'var(--text3)' }}>Direct</span>;
      },
    },
    {
      accessorKey: 'source', header: 'Source', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() === 'axiscare'
        ? pill('AxisCare', { background: 'var(--info-bg)', color: 'var(--info)' })
        : pill('Manual', { background: 'var(--bg2)', color: 'var(--text3)' }),
    },
    {
      accessorKey: 'status', header: 'Status', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => { const sc = statusColor(info.getValue()); return pill(info.getValue()?.replace('_', ' '), { background: sc.bg, color: sc.color }); },
    },
    {
      accessorKey: 'email', header: 'Email', size: 200,
      meta: { mobile: 'secondary', truncate: true },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'phone', header: 'Phone', size: 130, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'pay_rate', header: 'Pay Rate', size: 120,
      meta: { mobile: 'secondary' },
      cell: info => {
        const s = info.row.original;
        const isAgency = s.employment_type === 'agency';
        return s.pay_rate
          ? (
            <span style={{ color: 'var(--success)', fontWeight: 500 }} title={isAgency ? 'Agency bill rate — what AHFC pays the agency, not the worker\u2019s take-home' : undefined}>
              ${Number(s.pay_rate).toFixed(2)}
              <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text3)' }}>{{ hourly: '/hr', weekly: '/wk', bi_weekly: '/2wk', monthly: '/mo', annually: '/yr', per_visit: '/visit' }[s.pay_type] || ''}</span>
              {isAgency && <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--info)', marginLeft: 4 }}>BILL</span>}
            </span>
          )
          : <span style={{ fontSize: 11, background: 'var(--warn-bg)', color: 'var(--warn)', padding: '2px 7px', borderRadius: 4 }}>not set</span>;
      },
    },
    {
      accessorKey: 'profile_id', header: 'Login', size: 100,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue()
        ? <span style={{ fontSize: 11, background: 'var(--success-bg)', color: 'var(--success)', padding: '2px 7px', borderRadius: 20, fontWeight: 500 }}>✓ User</span>
        : <span style={{ fontSize: 11, color: 'var(--text3)' }}>No login</span>,
    },
    {
      id: 'actions', header: 'Actions', size: 220, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const s = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => goToProfile(s)}>View</button>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(s)}>Edit</button>
            {!s.axiscare_id && s.status === 'active' && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--warn)', borderColor: 'var(--warn-border)' }} onClick={() => pushToAxisCareRetry(s)} disabled={axisPushBusyId === s.id}
                title={s.onboarding_status === 'active'
                  ? 'AxisCare accounts are created automatically when the bundle is signed. This person has none — the auto-create failed (see the timeline on their profile) or they predate it. Push to create it now.'
                  : 'AxisCare account will be created automatically when they sign their bundle. Push now only if they need to clock in before signing.'}>
                {axisPushBusyId === s.id ? 'Pushing…' : s.onboarding_status === 'active' ? 'Push to AxisCare (auto-create missed)' : 'Push to AxisCare early'}
              </button>
            )}
            {s.source !== 'axiscare' && !s.profile_id && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--danger)', borderColor: 'var(--danger-border)' }} onClick={() => handleDelete(s)}>Remove</button>
            )}
            {!s.hidden && isAdmin && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--text3)' }} onClick={() => handleHide(s)}>Hide</button>
            )}
            {s.hidden && showHidden && isAdmin && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--success)', borderColor: 'var(--success-border)' }} onClick={async () => {
                await supabase.from('staff').update({ hidden: false }).eq('id', s.id);
                setSuccess(`${s.full_name} restored`);
                setTimeout(() => setSuccess(''), 3000);
                load();
              }}>Unhide</button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Success / error banners */}
      {success     && <div style={{ background:'var(--success-bg)', border:'0.5px solid var(--success-border)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--success)' }}>✓ {success}</div>}
      {axisWarning && <div style={{ background:'var(--warn-bg)',    border:'0.5px solid var(--warn-border)',    borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--warn)'    }}>⚠ {axisWarning}</div>}
      {axisError   && <div style={{ background:'var(--warn-bg)',    border:'0.5px solid var(--warn-border)',    borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12, color:'var(--warn)'    }}>⚠ {axisError}</div>}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,minmax(0,1fr))', gap:8, marginBottom:14 }}>
        {[
          { label:'Total Personnel', value:stats.total,    tone:'neutral' },
          { label:'Active',          value:stats.active,   tone:'neutral' },
          { label:'From AxisCare',   value:stats.axiscare, tone:'neutral' },
          { label:'Have Login',      value:stats.hasLogin, tone:'neutral' },
          { label:'Pay Rate Missing',value:stats.noRate,   tone:stats.noRate > 0 ? 'warn' : 'neutral' },
        ].map(s => <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />)}
      </div>

      <div className="card">
        {/* Toolbar has grown to 6+ action buttons (Quick Add added 2026-08-08) —
            .card-header's shared CSS has no flex-wrap, so at anything narrower
            than ~1300px the later buttons (Quick Add, +Add Personnel) were
            pushed off the visible edge with no scroll affordance, effectively
            invisible. Wrap here rather than in the shared class, scoped to
            this page's unusually button-heavy header. */}
        <div className="card-header" style={{ flexWrap: 'wrap', rowGap: 8 }}>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ fontSize:12, padding:'5px 10px', borderRadius:8, border:'0.5px solid var(--border)', background:'var(--bg2)', color:'var(--text)', fontFamily:'inherit', maxWidth:220 }}
          >
            <option value="All">All Positions</option>
            {roles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
          <select
            value={agencyFilter}
            onChange={e => setAgencyFilter(e.target.value)}
            style={{ fontSize:12, padding:'5px 10px', borderRadius:8, border:'0.5px solid var(--border)', background:'var(--bg2)', color:'var(--text)', fontFamily:'inherit', maxWidth:220 }}
          >
            <option value="All">All Sources</option>
            <option value="none">Direct only</option>
            <option value="any">Any agency</option>
            {contractingAgencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', rowGap:8 }}>
            <input
              placeholder="Search name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontSize:12, padding:'5px 10px', borderRadius:8, border:'0.5px solid var(--border)', background:'var(--bg2)', color:'var(--text)', width:180, fontFamily:'inherit' }}
            />
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setShowHidden(p => !p)}
              style={{ fontSize:11 }}
            >
              {showHidden ? '👁 Hide hidden' : '👁 Show hidden'}
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={syncAxisCare}
              disabled={syncing}
              title="Legacy intake path — new hires now come through Onboarding"
              style={{ display:'flex', alignItems:'center', gap:5 }}
            >
              {syncing
                ? <><div style={{ width:10, height:10, border:'1.5px solid var(--accent)', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.6s linear infinite' }} /> Syncing...</>
                : '↻ Sync AxisCare (legacy)'
              }
            </button>
            {lastSync && <span style={{ fontSize:10, color:'var(--text3)' }}>Last sync {lastSync.toLocaleTimeString()}</span>}
            <a href="/careers/apply" target="_blank" rel="noreferrer" className="btn btn-sm" style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:6, background:'var(--navy)', color:'#fff', borderRadius:20 }}>
              <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6"/><path d="M10 14 21 3"/></svg>
              Careers Page
            </a>
            <button className="btn btn-outline btn-sm" onClick={openQuickAdd} title="Urgent hire: create an active record now. The offer bundle still goes out from the Onboarding Pipeline for one signature.">⚡ Quick Add</button>
            <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Add Personnel</button>
          </div>
        </div>

        {missingDob && (
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'var(--warn-bg)', border:'1px solid var(--warn-border)', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:12.5, color:'var(--text2)' }}>
            Showing active staff with no date of birth on file ({filtered.length}). Open each to add it; birthdays on the dashboard come from here.
            <button className="btn btn-ghost btn-sm" style={{ marginLeft:'auto' }} onClick={() => navigate('/hr/staff-records')}>Show everyone</button>
          </div>
        )}
        <DataTable
          tableId="staff-records"
          columns={columns}
          data={filtered}
          getRowId={s => s.id}
          loading={loading}
          emptyMessage="No personnel found"
          maxHeight="calc(100vh - 320px)"
        />
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth:580 }}>
            <div className="modal-header">
              <div className="modal-title">{editing ? `Edit — ${editing.last_name}, ${editing.first_name}` : 'Add Personnel'}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight:'72vh', overflowY:'auto' }}>
              {error && <div style={{ background:'var(--danger-bg)', border:'0.5px solid var(--danger-border)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--danger)' }}>{error}</div>}

              {editing?.source === 'axiscare' && (
                <div style={{ background:'var(--info-bg)', border:'0.5px solid var(--info-border)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'var(--info)' }}>
                  This staff member was originally discovered via AxisCare, but CAERA is the source of truth now — edit freely here. Changing status pushes the update to AxisCare automatically.
                </div>
              )}

              <div className="form-row">
                <F label="First Name *">
                  <input className="form-input" value={form.first_name} onChange={e => setForm(p=>({...p,first_name:e.target.value}))} />
                </F>
                <F label="Last Name *">
                  <input className="form-input" value={form.last_name} onChange={e => setForm(p=>({...p,last_name:e.target.value}))} />
                </F>
              </div>

              <div className="form-row">
                <F label="Suffix">
                  <input className="form-input" value={form.suffix} onChange={e => setForm(p=>({...p,suffix:e.target.value}))} placeholder="Jr., Sr., III — optional" />
                </F>
                <F label="Goes By">
                  <input className="form-input" value={form.goes_by} onChange={e => setForm(p=>({...p,goes_by:e.target.value}))} placeholder="Preferred name — optional" />
                </F>
              </div>

              <div className="form-row">
                <F label="Date of Birth">
                  <input type="date" className="form-input" value={form.date_of_birth} onChange={e => setForm(p=>({...p,date_of_birth:e.target.value}))} />
                </F>
                <F label="SSN (last 4)">
                  <input type="text" inputMode="numeric" maxLength={4} className="form-input" placeholder="••••" value={form.ssn_last4}
                    onChange={e => setForm(p=>({...p,ssn_last4:e.target.value.replace(/\D/g,'').slice(0,4)}))}
                    title="Last four digits only — required on the Georgia EVV caregiver record" />
                </F>
                <F label="Gender">
                  <select className="form-select" value={form.gender} onChange={e => setForm(p=>({...p,gender:e.target.value}))}>
                    <option value="">Select...</option>
                    {GENDER_OPTS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </F>
              </div>

              {/* Checked against a home's household pets when this person is assigned (lib/pets.js). */}
              <div className="form-row">
                <F label="Cannot work around">
                  <div style={{ display:'flex', flexWrap:'wrap', gap:12, paddingTop:6 }}>
                    {RESTRICTION_OPTIONS.map(([key, label]) => (
                      <label key={key} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13, color:'var(--text)', cursor:'pointer' }}>
                        <input type="checkbox" checked={(form.pet_restrictions || []).includes(key)}
                          onChange={e => setForm(p => ({ ...p, pet_restrictions: e.target.checked
                            ? [...(p.pet_restrictions || []), key]
                            : (p.pet_restrictions || []).filter(k => k !== key) }))} />
                        {label}
                      </label>
                    ))}
                  </div>
                </F>
                <F label="Pet notes">
                  <input className="form-input" value={form.pet_restrictions_notes} placeholder="e.g. allergic to cats; fine with small dogs"
                    onChange={e => setForm(p => ({ ...p, pet_restrictions_notes: e.target.value }))} />
                </F>
              </div>

              <div className="form-row">
                <F label="Position">
                  <select className="form-select" value={form.position_code} onChange={e => setForm(p => ({
                    ...p,
                    position_code: e.target.value,
                    caregiver_relationship: e.target.value === 'PCA' ? p.caregiver_relationship : '',
                    // Clear tier-specific flags when Position moves away from the
                    // tier they apply to — otherwise a former Lead RN switched to
                    // PCA keeps showing "Lead", and a former Manager/Director keeps
                    // showing as licensure-relevant "Administrator of Record" after
                    // being reassigned to an unrelated role.
                    is_lead: (e.target.value === 'RN' || e.target.value === 'LPN') ? p.is_lead : false,
                    is_administrator_of_record: (e.target.value === 'MGR' || e.target.value === 'DIR_OPS') ? p.is_administrator_of_record : false,
                  }))}>
                    <option value="">Select...</option>
                    {roles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                  </select>
                </F>
                {form.position_code === 'PCA' && (
                  <F label="Caregiver Relationship">
                    <select className="form-select" value={form.caregiver_relationship} onChange={e => setForm(p=>({...p,caregiver_relationship:e.target.value}))}>
                      <option value="">Select...</option>
                      <option value="family">Family Caregiver</option>
                      <option value="independent">Independent</option>
                    </select>
                  </F>
                )}
              </div>
              {(form.position_code === 'RN' || form.position_code === 'LPN') && (
                <F label="Lead / Clinical Supervisor">
                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
                    <input type="checkbox" checked={form.is_lead} onChange={e => setForm(p=>({...p,is_lead:e.target.checked}))} />
                    Yes — this person is a lead / supervisory-tier {form.position_code}
                  </label>
                </F>
              )}
              {(form.position_code === 'MGR' || form.position_code === 'DIR_OPS') && (
                <F label="Administrator of Record">
                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
                    <input type="checkbox" checked={form.is_administrator_of_record} onChange={e => setForm(p=>({...p,is_administrator_of_record:e.target.checked}))} />
                    Yes — named administrator on the agency license
                  </label>
                </F>
              )}

              <div className="form-row">
                <F label="Email">
                  <input type="email" className="form-input" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))} />
                </F>
                <F label="Phone">
                  <input className="form-input" value={form.phone} onChange={e => setForm(p=>({...p,phone:e.target.value}))} />
                </F>
              </div>

              <F label="Street Address">
                <input className="form-input" value={form.address} onChange={e => setForm(p=>({...p,address:e.target.value}))} placeholder="123 Main St" />
              </F>
              <div className="form-row">
                <F label="City">
                  <input className="form-input" value={form.city} onChange={e => setForm(p=>({...p,city:e.target.value}))} />
                </F>
                <F label="County">
                  <select className="form-select" value={form.county} onChange={e => setForm(p=>({...p,county:e.target.value}))}>
                    <option value="">Select...</option>
                    {GA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </F>
              </div>
              <div className="form-row">
                <F label="State">
                  <input className="form-input" value={form.state} onChange={e => setForm(p=>({...p,state:e.target.value}))} maxLength={2} style={{ textTransform:'uppercase' }} />
                </F>
                <F label="ZIP">
                  <input className="form-input" value={form.zip} onChange={e => setForm(p=>({...p,zip:e.target.value}))} />
                </F>
              </div>

              <div className="form-row">
                <F label="Employment Type">
                  <select className="form-select" value={form.employment_type} onChange={e => setForm(p=>({...p,employment_type:e.target.value}))}>
                    {EMP_TYPES.map(t => <option key={t} value={t}>{t.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
                  </select>
                </F>
                <F label="Status">
                  <select className="form-select" value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                    {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
                  </select>
                </F>
              </div>

              {(form.employment_type === 'agency' || form.contracting_agency_id) && (
                <F label="Contracting Agency" hint={form.employment_type === 'agency'
                  ? 'Policy A-2.4 — one signed agreement per agency, not per person. Add/manage agencies under Training & Compliance → Contracting Agencies.'
                  : 'Employment Type is no longer “Agency” — saving now will clear this link.'}>
                  <select className="form-select" value={form.contracting_agency_id} onChange={e => setForm(p=>({...p,contracting_agency_id:e.target.value}))}>
                    <option value="">Select agency…</option>
                    {contractingAgencies.map(a => <option key={a.id} value={a.id}>{a.name}{a.status !== 'active' ? ` (${a.status})` : ''}</option>)}
                  </select>
                </F>
              )}

              <div className="form-row">
                <F
                  label={form.employment_type === 'agency' ? 'Agency Bill Rate' : 'Pay Rate'}
                  hint={(form.employment_type === 'agency' ? 'What AHFC pays the agency — not the worker’s take-home. ' : '') + ({'hourly':'Per hour','weekly':'Per week','bi_weekly':'Bi-weekly','monthly':'Per month','annually':'Annually','per_visit':'Per visit'}[form.pay_type] || '')}
                >
                  <div style={{ display:'flex', gap:8 }}>
                    <input type="number" step="0.01" className="form-input" value={form.pay_rate} onChange={e => setForm(p=>({...p,pay_rate:e.target.value}))} placeholder="0.00" style={{ flex:1 }} />
                    <select className="form-select" value={form.pay_type} onChange={e => setForm(p=>({...p,pay_type:e.target.value}))} style={{ width:130 }}>
                      <option value="hourly">Per hour</option>
                      <option value="weekly">Per week</option>
                      <option value="bi_weekly">Bi-weekly</option>
                      <option value="monthly">Per month</option>
                      <option value="annually">Annually</option>
                      <option value="per_visit">Per visit</option>
                    </select>
                  </div>
                </F>
                <F label="Start Date">
                  <input type="date" className="form-input" value={form.start_date} onChange={e => setForm(p=>({...p,start_date:e.target.value}))} />
                </F>
              </div>

              <div className="form-row">
                <F label="Preferred Language">
                  <select className="form-select" value={form.preferred_language} onChange={e => setForm(p=>({...p,preferred_language:e.target.value}))}>
                    {LANG_OPTS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </F>
                <F label="Supervisor">
                  <select className="form-select" value={form.supervisor_id} onChange={e => setForm(p=>({...p,supervisor_id:e.target.value}))}>
                    <option value="">None</option>
                    {staff.filter(s => s.status === 'active' && s.id !== editing?.id).sort((a,b)=>(a.last_name||'').localeCompare(b.last_name||'')).map(s => (
                      <option key={s.id} value={s.id}>{s.last_name}, {s.first_name}</option>
                    ))}
                  </select>
                </F>
              </div>

              {form.status === 'terminated' && (
                <>
                  <div className="form-row">
                    <F label="Termination Date">
                      <input type="date" className="form-input" value={form.termination_date} onChange={e => setForm(p=>({...p,termination_date:e.target.value}))} />
                    </F>
                    <F label="Rehire Eligible">
                      <select className="form-select" value={form.rehire_eligible} onChange={e => setForm(p=>({...p,rehire_eligible:e.target.value}))}>
                        <option value="">Not determined</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </F>
                  </div>
                  <F label="Termination Reason">
                    <textarea className="form-textarea" rows={2} value={form.termination_reason} onChange={e => setForm(p=>({...p,termination_reason:e.target.value}))} />
                  </F>
                </>
              )}

              <F label="Notes">
                <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))} />
              </F>

              {editing?.profile_id && (
                <div style={{ background:'var(--bg2)', border:'0.5px solid var(--border)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--text2)' }}>
                  <strong>CAERA login:</strong> This staff member has a user account. To change their login role or module access, use <strong>Settings → User Management</strong>.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Personnel'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Add Modal (urgent-hire fast track) ── */}
      {showQuickAdd && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowQuickAdd(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">⚡ Quick Add</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowQuickAdd(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
              {quickError && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{quickError}</div>}
              <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--warn)' }}>
                This creates an active record immediately so they can be scheduled and paid today. They land in the Onboarding Pipeline's Candidate lane with "Already working" pre-set — send their bundle from there. Signing it creates their AxisCare account and sends the welcome email automatically; use "Push to AxisCare early" from the Actions column only if they must clock in before they have signed.
              </div>

              <div className="form-row">
                <F label="First Name *">
                  <input className="form-input" value={quickForm.first_name} onChange={e => setQuickForm(p => ({ ...p, first_name: e.target.value }))} />
                </F>
                <F label="Last Name *">
                  <input className="form-input" value={quickForm.last_name} onChange={e => setQuickForm(p => ({ ...p, last_name: e.target.value }))} />
                </F>
              </div>

              <div className="form-row">
                <F label="Phone">
                  <input className="form-input" value={quickForm.phone} onChange={e => setQuickForm(p => ({ ...p, phone: e.target.value }))} />
                </F>
                <F label="Email">
                  <input type="email" className="form-input" value={quickForm.email} onChange={e => setQuickForm(p => ({ ...p, email: e.target.value }))} />
                </F>
              </div>

              <F label="Date of Birth">
                <input type="date" className="form-input" value={quickForm.date_of_birth} onChange={e => setQuickForm(p => ({ ...p, date_of_birth: e.target.value }))} />
              </F>

              <F label="Street Address">
                <input className="form-input" value={quickForm.address} onChange={e => setQuickForm(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St" />
              </F>
              <div className="form-row">
                <F label="City">
                  <input className="form-input" value={quickForm.city} onChange={e => setQuickForm(p => ({ ...p, city: e.target.value }))} />
                </F>
                <F label="County">
                  <select className="form-select" value={quickForm.county} onChange={e => setQuickForm(p => ({ ...p, county: e.target.value }))}>
                    <option value="">Select...</option>
                    {GA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </F>
              </div>
              <div className="form-row">
                <F label="State">
                  <input className="form-input" value={quickForm.state} onChange={e => setQuickForm(p => ({ ...p, state: e.target.value }))} maxLength={2} style={{ textTransform: 'uppercase' }} />
                </F>
                <F label="ZIP">
                  <input className="form-input" value={quickForm.zip} onChange={e => setQuickForm(p => ({ ...p, zip: e.target.value }))} />
                </F>
              </div>

              <div className="form-row">
                <F label="Position">
                  <select className="form-select" value={quickForm.position_code} onChange={e => setQuickForm(p => ({ ...p, position_code: e.target.value, caregiver_relationship: e.target.value === 'PCA' ? p.caregiver_relationship : '' }))}>
                    <option value="">Select...</option>
                    {roles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                  </select>
                </F>
                {quickForm.position_code === 'PCA' && (
                  <F label="Caregiver Relationship">
                    <select className="form-select" value={quickForm.caregiver_relationship} onChange={e => setQuickForm(p => ({ ...p, caregiver_relationship: e.target.value }))}>
                      <option value="">Select...</option>
                      <option value="family">Family Caregiver</option>
                      <option value="independent">Independent</option>
                    </select>
                  </F>
                )}
              </div>

              <div className="form-row">
                <F label="Employment Type">
                  <select className="form-select" value={quickForm.employment_type} onChange={e => setQuickForm(p => ({ ...p, employment_type: e.target.value }))}>
                    {EMP_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                </F>
                <F label="Start Date">
                  <input type="date" className="form-input" value={quickForm.start_date} onChange={e => setQuickForm(p => ({ ...p, start_date: e.target.value }))} />
                </F>
              </div>

              {quickForm.employment_type === 'agency' && (
                <F label="Contracting Agency" hint="Policy A-2.4 — one signed agreement per agency, not per person.">
                  <select className="form-select" value={quickForm.contracting_agency_id} onChange={e => setQuickForm(p => ({ ...p, contracting_agency_id: e.target.value }))}>
                    <option value="">Select agency…</option>
                    {contractingAgencies.map(a => <option key={a.id} value={a.id}>{a.name}{a.status !== 'active' ? ` (${a.status})` : ''}</option>)}
                  </select>
                </F>
              )}

              <F
                label={quickForm.employment_type === 'agency' ? 'Agency Bill Rate' : 'Pay Rate'}
                hint={(quickForm.employment_type === 'agency' ? 'What AHFC pays the agency — not the worker’s take-home. ' : '') + ({ 'hourly': 'Per hour', 'weekly': 'Per week', 'bi_weekly': 'Bi-weekly', 'monthly': 'Per month', 'annually': 'Annually', 'per_visit': 'Per visit' }[quickForm.pay_type] || '')}
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="number" step="0.01" className="form-input" value={quickForm.pay_rate} onChange={e => setQuickForm(p => ({ ...p, pay_rate: e.target.value }))} placeholder="0.00" style={{ flex: 1 }} />
                  <select className="form-select" value={quickForm.pay_type} onChange={e => setQuickForm(p => ({ ...p, pay_type: e.target.value }))} style={{ width: 130 }}>
                    <option value="hourly">Per hour</option>
                    <option value="weekly">Per week</option>
                    <option value="bi_weekly">Bi-weekly</option>
                    <option value="monthly">Per month</option>
                    <option value="annually">Annually</option>
                    <option value="per_visit">Per visit</option>
                  </select>
                </div>
              </F>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowQuickAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleQuickSave} disabled={quickSaving}>{quickSaving ? 'Creating…' : '⚡ Create Personnel Record'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
